import API_URL from '../config';
import { getAuthHeaders, getStoredAccessToken } from './authSession';

const INSTALL_FLAG = '__putpensBrowserSecurityTrackerInstalled';
const SENSITIVE_KEY_PATTERN = /(password|token|cookie|secret|authorization|api[-_]?key|refresh[-_]?token|session|localstorage|sessionstorage)/i;
const SENSITIVE_TEXT_PATTERNS = [
  /((?:password|token|cookie|secret|authorization|api[-_]?key|refresh[-_]?token|session)\s*[:=]\s*)(?:bearer\s+)?([^\s,;]+)/gi,
  /((?:bearer)\s+)([a-z0-9\-._~+/]+=*)/gi,
];

const sanitizeText = (value) => {
  const normalized = String(value || '').trim();
  return SENSITIVE_TEXT_PATTERNS.reduce(
    (current, pattern) => current.replace(pattern, '$1[redacted]'),
    normalized
  );
};

const extractVersion = (userAgent, pattern) => {
  const match = userAgent.match(pattern);
  return match && match[1] ? match[1] : 'unknown';
};

export const detectBrowser = (userAgent = '') => {
  const normalized = String(userAgent || '');
  if (/edg\//i.test(normalized)) {
    return { browserName: 'Edge', browserVersion: extractVersion(normalized, /edg\/([\d.]+)/i) };
  }
  if (/firefox\//i.test(normalized)) {
    return { browserName: 'Firefox', browserVersion: extractVersion(normalized, /firefox\/([\d.]+)/i) };
  }
  if (/chrome\//i.test(normalized) && !/edg\//i.test(normalized)) {
    return { browserName: 'Chrome', browserVersion: extractVersion(normalized, /chrome\/([\d.]+)/i) };
  }
  if (/safari\//i.test(normalized) && !/chrome\//i.test(normalized) && !/edg\//i.test(normalized)) {
    return { browserName: 'Safari', browserVersion: extractVersion(normalized, /version\/([\d.]+)/i) };
  }
  return { browserName: 'unknown', browserVersion: 'unknown' };
};

const safeSerialize = (value, depth = 0, seen = new WeakSet()) => {
  if (value === null || value === undefined) return value;
  if (typeof value === 'string') return sanitizeText(value);
  if (typeof value === 'number' || typeof value === 'boolean') return value;
  if (typeof value === 'function' || typeof value === 'symbol') return undefined;
  if (depth >= 2) return '[omitted]';
  if (Array.isArray(value)) {
    return value.slice(0, 5).map((entry) => safeSerialize(entry, depth + 1, seen)).filter((entry) => entry !== undefined);
  }
  if (typeof value === 'object') {
    if (seen.has(value)) return '[circular]';
    seen.add(value);
    return Object.entries(value).reduce((output, [key, entry]) => {
      if (SENSITIVE_KEY_PATTERN.test(key)) {
        output[key] = '[redacted]';
        return output;
      }
      if (key === 'target' || key === 'currentTarget' || key === 'srcElement') return output;
      const serialized = safeSerialize(entry, depth + 1, seen);
      if (serialized !== undefined) output[key] = serialized;
      return output;
    }, {});
  }
  return sanitizeText(String(value));
};

const buildBasePayload = () => {
  const userAgent = typeof navigator !== 'undefined' ? navigator.userAgent : '';
  const browserInfo = detectBrowser(userAgent);
  return {
    timestamp: new Date().toISOString(),
    route: `${window.location.pathname || ''}${window.location.search || ''}`,
    browserName: browserInfo.browserName,
    browserVersion: browserInfo.browserVersion,
    userAgent: sanitizeText(userAgent),
    companyId: localStorage.getItem('company_id') || undefined,
  };
};

const sendPayload = (payload) => {
  const safePayload = safeSerialize(payload);
  if (!safePayload) return Promise.resolve();

  const token = getStoredAccessToken();
  if (!token) return Promise.resolve();

  const headers = {
    'Content-Type': 'application/json',
    ...getAuthHeaders(token),
  };

  return fetch(`${API_URL}/api/browser-security/errors/`, {
    method: 'POST',
    headers,
    body: JSON.stringify(safePayload),
    keepalive: true,
  }).then((response) => {
    if (response.status === 401) return null;
    return response;
  }).catch(() => {});
};

export const installBrowserSecurityTracker = () => {
  if (typeof window === 'undefined') return () => {};
  if (window[INSTALL_FLAG]) return window[INSTALL_FLAG];

  const previousOnError = typeof window.onerror === 'function' ? window.onerror : null;
  const handleWindowError = (message, source, lineno, colno, error) => {
    void sendPayload({
      ...buildBasePayload(),
      type: 'window.onerror',
      errorMessage: sanitizeText(message),
      source: sanitizeText(source),
      line: Number.isFinite(Number(lineno)) ? Number(lineno) : undefined,
      column: Number.isFinite(Number(colno)) ? Number(colno) : undefined,
      stack: error?.stack ? sanitizeText(error.stack) : undefined,
    });
    return previousOnError ? previousOnError.call(window, message, source, lineno, colno, error) : false;
  };

  const handleUnhandledRejection = (event) => {
    const reason = event?.reason;
    void sendPayload({
      ...buildBasePayload(),
      type: 'unhandledrejection',
      errorMessage: reason instanceof Error ? sanitizeText(reason.message) : sanitizeText(reason || 'Unhandled promise rejection'),
      stack: reason instanceof Error && reason.stack ? sanitizeText(reason.stack) : undefined,
      details: reason && !(reason instanceof Error) ? safeSerialize(reason) : undefined,
    });
  };

  window.onerror = handleWindowError;
  window.addEventListener('unhandledrejection', handleUnhandledRejection);

  const cleanup = () => {
    if (window.onerror === handleWindowError) window.onerror = previousOnError;
    window.removeEventListener('unhandledrejection', handleUnhandledRejection);
    delete window[INSTALL_FLAG];
  };

  window[INSTALL_FLAG] = cleanup;
  return cleanup;
};
